import { useEffect, useRef, useState } from "react";
import type { TodayTotals, DisplayStats } from "../data/replay";

/**
 * Today's volume — total across all products since midnight GMT.
 *
 * Ticks live as arcs / pulses emit on the map (App.tsx maintains a
 * baseline + per-event increments; we just render whatever lands here).
 * A secondary "+ N NEW CARDS" line surfaces Eversend's growth story
 * without cluttering the financial totals.
 */
export function VolumePulse({
  totals,
  displayStats,
}: {
  totals: TodayTotals | null;
  displayStats: DisplayStats | null;
}) {
  const target = totals?.total.volumeUsd ?? 0;
  const [display, setDisplay] = useState(target);
  const rafRef = useRef<number>();
  const fromRef = useRef(target);
  const startRef = useRef(performance.now());
  const DURATION = 700;

  useEffect(() => {
    fromRef.current = display;
    startRef.current = performance.now();
    const step = () => {
      const t = Math.min(1, (performance.now() - startRef.current) / DURATION);
      const ease = 1 - Math.pow(1 - t, 3);
      setDisplay(fromRef.current + (target - fromRef.current) * ease);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const cardsCreated = totals?.byProduct.card_creation.count ?? 0;
  const uniqueUsers = displayStats?.uniqueUsersToday ?? 0;
  const firstSenders = displayStats?.firstSendersToday ?? 0;
  const yesterdayVol = displayStats?.yesterdayVolumeUsd ?? 0;
  // For the % line we use the BQ-anchored today volume (both sides bounded
  // to MAX(transaction_date)) so the comparison is apples-to-apples to the
  // second. The headline PROCESSED · TODAY number above keeps using the
  // live counter so it ticks visibly.
  const todayVolForPct = displayStats?.todayVolumeUsd ?? 0;
  const pct =
    yesterdayVol > 0
      ? ((todayVolForPct - yesterdayVol) / yesterdayVol) * 100
      : 0;
  const hourGMT = new Date().getUTCHours();

  // Month-over-month pace (same-elapsed-time comparison).
  const mtdCur = displayStats?.mtdCurrentUsd ?? 0;
  const mtdPrev = displayStats?.mtdPrevUsd ?? 0;
  const curMonth = (displayStats?.currentMonthName ?? "").toUpperCase();
  const prevMonth = (displayStats?.prevMonthName ?? "").toUpperCase();
  const monthPct = mtdPrev > 0 ? ((mtdCur - mtdPrev) / mtdPrev) * 100 : 0;
  const dayOfMonth = new Date().getUTCDate();
  // Skip the first 2 days of the month — too little data for a meaningful ratio.
  const showMonthPace =
    mtdPrev > 0 && curMonth && prevMonth && dayOfMonth >= 2;

  return (
    <div className="volume-pulse">
      <div className="volume-label">PROCESSED · TODAY</div>
      <div className="volume-value">${formatInt(display)}</div>
      {hourGMT >= 6 && yesterdayVol > 0 && (
        <div
          className={`volume-comparison ${pct >= 0 ? "positive" : "negative"}`}
        >
          {pct >= 0 ? "+" : ""}
          {pct.toFixed(2)}% vs yesterday
        </div>
      )}
      {showMonthPace && (
        <div
          className={`volume-comparison ${monthPct >= 0 ? "positive" : "negative"}`}
        >
          {curMonth} PACE · {monthPct >= 0 ? "+" : ""}
          {monthPct.toFixed(2)}% vs {prevMonth}
        </div>
      )}
      {cardsCreated > 0 && (
        <div className="volume-growth">
          +{cardsCreated.toLocaleString("en-US")} NEW CARDS
        </div>
      )}
      {uniqueUsers > 0 && (
        <div className="volume-active">
          {uniqueUsers.toLocaleString("en-US")} USERS ACTIVE
        </div>
      )}
      {firstSenders > 0 && (
        <div className="volume-first-senders">
          +{firstSenders.toLocaleString("en-US")} FIRST TRANSACTORS
        </div>
      )}
    </div>
  );
}

function formatInt(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}
